import { api, unwrap } from '../../api/client';
import { useApiQuery } from '../../api/hooks';

// Shapes the bank routes return (apps/api/src/bank.ts). The generated schema leaves most of these
// responses untyped, so they are described here from the route code.

export type BankKind = 'checking' | 'savings' | 'credit_card' | 'cash';
export type BankAccount = {
  id: string;
  code: string;
  name: string;
  type: 'asset' | 'liability';
  system_key: string | null;
  kind: BankKind | null;
  last4: string | null;
  institution: string | null;
  is_bank: boolean;
  /** The account's currency (ADR 0029): its balances, statements and lines are in it. Absent on old fixtures: the base currency. */
  currency?: string;
  /** False once archived (ADR 0030). */
  is_active?: boolean;
  /** Never used, so it can be deleted outright rather than archived. */
  deletable?: boolean;
  ledger_balance: string;
  /** The ledger balance in the base currency. */
  base_balance?: string;
  /** For an account in another currency, the latest rate on or before today and its date (APP-DESIGN.md 9.5). */
  latest_rate?: string | null;
  latest_rate_date?: string | null;
  statement_balance: string | null;
  statement_date: string | null;
  statement_ledger_balance: string | null;
  unmatched_count: string;
  line_count: string;
  open_reconciliation_id: string | null;
  last_reconciled_date: string | null;
  last_reconciled_balance: string | null;
};
export type LineStatus = 'unmatched' | 'matched' | 'excluded';
export type BankLine = {
  id: string;
  date: string;
  amount: string;
  description: string;
  reference: string | null;
  payee: string | null;
  source: 'csv' | 'ofx' | 'manual' | 'feed';
  status: LineStatus;
  reconciliation_id: string | null;
  matches: { journal_line_id: string; amount: string }[];
  suggested_category?: { account_id: string; code: string; name: string; times: number; reason: string } | null;
};
export type LedgerLine = {
  journal_line_id: string;
  journal_id: string;
  journal_number: string | number;
  date: string;
  amount: string;
  memo: string | null;
  source_type: string;
  reference: string | null;
  contact_name: string | null;
  matched: boolean;
};
export type Suggestion = {
  statement_line_id: string;
  journal_line_id: string;
  days_apart: number;
  score: number;
  confidence: 'high' | 'medium';
  reason: string;
  statement_date: string;
  amount: string;
  description: string;
  ledger_date: string;
  ledger_memo: string | null;
  ledger_reference: string | null;
  contact_name: string | null;
  journal_number: string | number;
};
/** An open bill or invoice a line could pay (suggest_document_matches / open-documents, migration 0027). */
export type OpenDocument = {
  document_id: string;
  kind: 'bill' | 'invoice';
  document_number: string;
  reference: string | null;
  contact_id: string;
  contact_name: string;
  issue_date: string;
  due_date: string;
  amount_due: string;
  /** The document's currency; amount_due is in it (ADR 0029). */
  currency?: string;
};
export type DocSuggestion = OpenDocument & {
  statement_line_id: string;
  days_from_due: number;
  score: number;
  confidence: 'high' | 'medium';
  reason: string;
};
export type Mapping = {
  date?: string;
  description?: string;
  description_also?: string[];
  amount?: string;
  debit?: string;
  credit?: string;
  reference?: string;
  payee?: string;
  balance?: string;
  date_order?: 'mdy' | 'dmy' | 'ymd';
  invert?: boolean;
};
export type ImportPreview = {
  format: 'csv' | 'ofx';
  total: number;
  already_imported: number;
  skipped: { row: number; reason: string }[];
  ledger_balance: string | null;
  ledger_balance_date: string | null;
  preview: { date: string; amount: string; description: string; reference?: string; payee?: string; already_imported: boolean }[];
};
export type ImportResult = {
  id: string;
  line_count: number | string;
  inserted_count: number | string;
  skipped_duplicates: number;
  skipped: { row: number; reason: string }[];
};
export type RecLine = { id: string; date: string; amount: string; description: string; status: LineStatus; cleared: boolean };
export type Reconciliation = {
  id: string;
  account_id: string;
  account_name: string;
  statement_date: string;
  statement_balance: string;
  opening_balance: string;
  status: 'open' | 'completed';
  created_at: string;
  created_by: string | null;
  created_by_name: string | null;
  completed_at: string | null;
  completed_by: string | null;
  completed_by_name: string | null;
  undone_at: string | null;
  undone_by: string | null;
  undone_by_name: string | null;
};
export type RecDetail = Reconciliation & {
  cleared_total: string;
  cleared_balance: string;
  difference: string;
  cleared_count: number;
  uncleared_count: number;
  lines: RecLine[];
};

/** The `data` of a response, typed from the route code where the generated schema has none. */
async function dataOf<T>(call: Promise<{ data?: unknown; error?: unknown; response: Response }>): Promise<T> {
  return ((await unwrap(call)) as unknown as { data: T }).data;
}

export const getBankAccounts = (all = false) =>
  dataOf<BankAccount[]>(api.GET('/v1/bank-accounts', { params: { query: all ? { all: 'true' } : {} } }));
export const getArchivedBankAccounts = () => dataOf<BankAccount[]>(api.GET('/v1/bank-accounts', { params: { query: { archived: 'true' } } }));
const accountPath = (id: string) => ({ params: { path: { id } } });
export const archiveBankAccount = (id: string, clearExchangeDifference = false) =>
  dataOf<BankAccount>(api.POST('/v1/bank-accounts/{id}/archive', { ...accountPath(id), body: clearExchangeDifference ? { clear_exchange_difference: true } : {} }));
export const restoreBankAccount = (id: string) => dataOf<BankAccount>(api.POST('/v1/bank-accounts/{id}/restore', accountPath(id)));
export const deleteBankAccount = async (id: string) => { await unwrap(api.DELETE('/v1/bank-accounts/{id}', accountPath(id))); };
export const undoAllReconciliations = (id: string, from?: string) =>
  dataOf<{ undone: number }>(api.POST('/v1/bank-accounts/{id}/reconciliations/undo-all', { ...accountPath(id), body: from ? { from } : {} }));
export type BankAccountInput = { name: string; kind: BankKind; last4?: string | null; institution?: string | null; currency?: string };
export const createBankAccount = (body: BankAccountInput) => dataOf<BankAccount>(api.POST('/v1/bank-accounts', { body }));
export const updateBankAccount = (id: string, body: { name?: string; last4?: string | null; institution?: string | null; currency?: string }) =>
  dataOf<BankAccount>(api.PATCH('/v1/bank-accounts/{id}', { params: { path: { id } }, body }));
/** Narrows a list on the server, so lines older than the latest 500 are found too. Amounts are sizes, in or out. */
export type LineFilter = { amount_min?: string; amount_max?: string; from?: string; to?: string; reconciled?: 'true' | 'false' };
export const getLines = (account_id: string, status: LineStatus, filter: LineFilter = {}) =>
  dataOf<BankLine[]>(api.GET('/v1/bank-lines', { params: { query: { account_id, status, limit: 500, ...filter } } }));
/** The most lines the Banking list loads for one status: ten pages of the API's 500. */
export const MAX_LIST_LINES = 5000;
/** Every line of a status, newest first, fetched 500 at a time, so the list can page and count them all. */
export async function getAllLines(account_id: string, status: LineStatus, filter: LineFilter = {}): Promise<BankLine[]> {
  const all: BankLine[] = [];
  for (let offset = 0; offset < MAX_LIST_LINES; offset += 500) {
    const page = await dataOf<BankLine[]>(api.GET('/v1/bank-lines', { params: { query: { account_id, status, limit: 500, offset, ...filter } } }));
    all.push(...page);
    if (page.length < 500) break;
  }
  return all;
}
export const getSuggestions = (id: string) => dataOf<Suggestion[]>(api.GET('/v1/bank-accounts/{id}/suggestions', { params: { path: { id } } }));
export const getLedgerLines = (id: string, unmatched = false) =>
  dataOf<LedgerLine[]>(api.GET('/v1/bank-accounts/{id}/ledger-lines', { params: { path: { id }, query: unmatched ? { unmatched: 'true' } : {} } }));

export const importStatement = <T>(id: string, body: { content: string; filename: string; mapping?: Mapping; dry_run: boolean }) =>
  dataOf<T>(api.POST('/v1/bank-accounts/{id}/imports', { params: { path: { id } }, body }));
export const addLine = (body: { account_id: string; date: string; amount: string; description: string; reference?: string; payee?: string }) =>
  dataOf<BankLine>(api.POST('/v1/bank-lines', { body }));
export const deleteLine = async (id: string) => { await unwrap(api.DELETE('/v1/bank-lines/{id}', { params: { path: { id } } })); };
/** Several lines at once, all or none; none may be matched or reconciled. */
export const deleteLines = async (ids: string[]) => (await unwrap(api.POST('/v1/bank-lines/bulk-delete', { body: { ids } }))).deleted;
export type Removed = { entries_reversed: number; payments_voided: number; entries_kept: number };
/** Remove & redo: unmatch, reversing a categorised entry or voiding a payment behind the match. */
export const removeAndRedo = async (id: string): Promise<Removed> =>
  (await unwrap(api.POST('/v1/bank-lines/{id}/remove-and-redo', { params: { path: { id } } }))).removed;
export const setExcluded = (id: string, excluded: boolean) =>
  dataOf<BankLine>(api.POST('/v1/bank-lines/{id}/exclude', { params: { path: { id } }, body: { excluded } }));
export const matchLine = (id: string, journal_line_ids: string[]) =>
  dataOf<BankLine>(api.POST('/v1/bank-lines/{id}/match', { params: { path: { id } }, body: { journal_line_ids } }));
export const unmatchLine = (id: string) => dataOf<BankLine>(api.POST('/v1/bank-lines/{id}/unmatch', { params: { path: { id } } }));
/** exchange_rate: for a line on a foreign account. amount: what a bank account in another currency received or paid, in its own. */
export const categoriseLine = (id: string, account_id: string, fx: { exchange_rate?: string; amount?: string } = {}) =>
  dataOf<BankLine>(api.POST('/v1/bank-lines/{id}/categorise', { params: { path: { id } }, body: { account_id, ...fx } }));
export const acceptSuggestions = (id: string, pairs?: { statement_line_id: string; journal_line_id: string }[]) =>
  dataOf<{ matched: number }>(api.POST('/v1/bank-accounts/{id}/suggestions/accept', { params: { path: { id } }, body: pairs ? { pairs } : {} }));
export const getDocSuggestions = (id: string) =>
  dataOf<DocSuggestion[]>(api.GET('/v1/bank-accounts/{id}/document-suggestions', { params: { path: { id } } }));
export const getOpenDocuments = (id: string, q: string) =>
  dataOf<OpenDocument[]>(api.GET('/v1/bank-lines/{id}/open-documents', { params: { path: { id }, query: q ? { q } : {} } }));
/** Pay documents from a line and match it to the payment, in one step. */
export const payLine = (id: string, allocations: { document_id: string; amount: string; bank_amount?: string }[], exchange_rate?: string) =>
  dataOf<BankLine>(api.POST('/v1/bank-lines/{id}/pay', { params: { path: { id } }, body: { allocations, ...(exchange_rate ? { exchange_rate } : {}) } }));

export const listRecs = (account_id: string) => dataOf<Reconciliation[]>(api.GET('/v1/reconciliations', { params: { query: { account_id } } }));
export const getRec = (id: string) => dataOf<RecDetail>(api.GET('/v1/reconciliations/{id}', { params: { path: { id } } }));
export const startRec = (body: { account_id: string; statement_date: string; statement_balance: string; opening_balance?: string }) =>
  dataOf<RecDetail>(api.POST('/v1/reconciliations', { body }));
export const updateRec = (id: string, body: { statement_date?: string; statement_balance?: string }) =>
  dataOf<RecDetail>(api.PATCH('/v1/reconciliations/{id}', { params: { path: { id } }, body }));
export const discardRec = async (id: string) => { await unwrap(api.DELETE('/v1/reconciliations/{id}', { params: { path: { id } } })); };
export const completeRec = (id: string) => dataOf<RecDetail>(api.POST('/v1/reconciliations/{id}/complete', { params: { path: { id } } }));
export const undoRec = (id: string) => dataOf<RecDetail>(api.POST('/v1/reconciliations/{id}/undo', { params: { path: { id } } }));

// ---- Queries (all under the 'bank' key, so one invalidate('bank') refreshes them) ----
export const useBankAccounts = () => useApiQuery(['bank', 'accounts'], () => getBankAccounts());
/** Every account a statement can go to, for the import and manual-line dialogs. */
export const useImportAccounts = () => useApiQuery(['bank', 'accounts', 'all'], () => getBankAccounts(true));
export const useArchivedBankAccounts = (enabled = true) => useApiQuery(['bank', 'accounts', 'archived'], getArchivedBankAccounts, { enabled });
export const useLines = (account: string | undefined, status: LineStatus, filter: LineFilter = {}) =>
  useApiQuery(['bank', 'lines', account, status, filter], () => getLines(account as string, status, filter), { enabled: !!account });
export const useAllLines = (account: string | undefined, status: LineStatus, filter: LineFilter = {}) =>
  useApiQuery(['bank', 'lines-all', account, status, filter], () => getAllLines(account as string, status, filter), { enabled: !!account });
export const useSuggestions = (account: string | undefined) =>
  useApiQuery(['bank', 'suggestions', account], () => getSuggestions(account as string), { enabled: !!account });
export const useDocSuggestions = (account: string | undefined) =>
  useApiQuery(['bank', 'doc-suggestions', account], () => getDocSuggestions(account as string), { enabled: !!account });
export const useLedgerLines = (account: string | undefined) =>
  useApiQuery(['bank', 'ledger', account], () => getLedgerLines(account as string), { enabled: !!account });
export const useRecs = (account: string | undefined) =>
  useApiQuery(['bank', 'recs', account], () => listRecs(account as string), { enabled: !!account });
export const useRec = (id: string | null | undefined) => useApiQuery(['bank', 'rec', id], () => getRec(id as string), { enabled: !!id });
